/**
 * One store per open session. Folds every frame from the socket through
 * the canonical ACP v2 projection and the bridge reducer, coalesces
 * notifications to one per animation frame so token deltas do not re-render
 * the tree hundreds of times a second, and exposes a useSyncExternalStore
 * contract.
 */

import { api } from '@web/api/client'
import { SessionSocket, type SocketStatus } from '@web/api/socket'
import type {
  Command,
  FrameRecord,
  QueuedMessage,
  SessionArtifact,
  SessionRecord,
} from '@web/api/types'
import { type AcpState, applyAcpFrame, initialAcpState } from '@web/state/acp'
import {
  applyBridgeFrame,
  type BridgeState,
  costHighWater,
  initialBridgeState,
} from '@web/state/bridge'
import { markStartup, observeStartupFrame } from '@web/state/startup-telemetry'

export type Snapshot = {
  acp: AcpState
  bridge: BridgeState
  socket: SocketStatus
  lastSeq: number
  hydrating: boolean
  historyThrough: number
  historicalMessageIds: ReadonlySet<string>
  /** Local feedback while a dispatched prompt awaits the agent's running state. */
  optimisticWorking?: boolean
  /** Bumped when anything changes; cheap equality for memo hooks. */
  version: number
}
export type ComposerAttachment = { upload?: SessionArtifact } & (
  | { id: string; kind: 'image'; name: string; mediaType: string; data: string }
  | { id: string; kind: 'text'; name: string; text: string }
  | { id: string; kind: 'file'; name: string; mediaType: string; data: string }
)
type ComposerDraft = { text: string; attachments: ComposerAttachment[] }
export type FactoryTaskContext = { id: string; title: string }
type StartupOption = {
  id: string
  config_id: string
  value: string | boolean
}
type StartupOutbox = {
  options: StartupOption[]
  messages: QueuedMessage[]
}

const startupOutboxKey = (sessionId: string) =>
  `bridge.startup-outbox.${sessionId}`
const titleMessagesKey = (sessionId: string) =>
  `bridge.title-messages.${sessionId}`

function loadTitleMessages(sessionId: string): Map<string, string> {
  try {
    const entries = JSON.parse(
      sessionStorage.getItem(titleMessagesKey(sessionId)) ?? '[]',
    )
    return new Map(
      Array.isArray(entries)
        ? entries.filter(
            (entry): entry is [string, string] =>
              Array.isArray(entry) &&
              typeof entry[0] === 'string' &&
              typeof entry[1] === 'string',
          )
        : [],
    )
  } catch {
    return new Map()
  }
}

/** Read a same-tab startup outbox without letting corrupt storage block a session. */
function loadStartupOutbox(sessionId: string): StartupOutbox {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(startupOutboxKey(sessionId)) ?? '',
    )
    if (!Array.isArray(saved?.options) || !Array.isArray(saved?.messages))
      return { options: [], messages: [] }
    return {
      options: saved.options.filter(
        (item: unknown): item is StartupOption =>
          !!item &&
          typeof item === 'object' &&
          typeof (item as StartupOption).id === 'string' &&
          typeof (item as StartupOption).config_id === 'string' &&
          ['string', 'boolean'].includes(typeof (item as StartupOption).value),
      ),
      messages: saved.messages.filter(
        (item: unknown): item is QueuedMessage =>
          !!item &&
          typeof item === 'object' &&
          typeof (item as QueuedMessage).id === 'string' &&
          typeof (item as QueuedMessage).text === 'string' &&
          typeof (item as QueuedMessage).created_at === 'number',
      ),
    }
  } catch {
    return { options: [], messages: [] }
  }
}

export class SessionStore {
  private awaitingActivity = new Set<string>()
  private serverAccepted = new Set<string>()
  private outgoing = new Map<
    string,
    QueuedMessage & { placement: 'chat' | 'queue' }
  >()

  /** Project unacknowledged chat messages without modifying canonical history. */
  getDisplayState = (snapshot: Snapshot = this.snap): AcpState => {
    let state = snapshot.acp
    const pending = new Map(this.outgoing)
    for (const item of this.startupOutbox.messages)
      if (!pending.has(item.id))
        pending.set(item.id, { ...item, placement: 'chat' })
    const received = new Set(
      Object.values(state.messages).map((message) => message.requestId),
    )
    for (const item of pending.values()) {
      if (item.placement !== 'chat' || received.has(item.id)) continue
      const displayContent = item.display_content ?? item.content
      const content = displayContent?.length
        ? displayContent
        : [{ type: 'text', text: item.text }]
      state = applyAcpFrame(
        state,
        {
          type: 'agent/update',
          request_id: item.id,
          update: {
            sessionUpdate: 'user_message',
            messageId: 'optimistic:' + item.id,
            content,
            _meta: {
              'dev.agentbridge': {
                sendMode: item.startup ? 'normal' : item.send_mode,
                origin: item.origin,
              },
            },
          },
        },
        snapshot.lastSeq + 1,
        item.created_at,
      )
    }
    return state
  }

  isOptimistic = (requestId?: string): boolean =>
    !!requestId &&
    !this.serverAccepted.has(requestId) &&
    (this.outgoing.has(requestId) ||
      this.startupOutbox.messages.some((item) => item.id === requestId))

  isStartupMessage = (id: string): boolean =>
    id.startsWith('initial:') ||
    !!this.outgoing.get(id)?.startup ||
    this.startupOutbox.messages.some((item) => item.id === id) ||
    this.snap.bridge.queue.some(
      (item) => item.id === id && item.startup === true,
    )

  getPendingMessages = (): QueuedMessage[] => [
    ...[...this.outgoing.values()].filter((item) => item.placement === 'queue'),
  ]
  private snap: Snapshot = {
    acp: initialAcpState(),
    bridge: initialBridgeState(),
    socket: 'connecting',
    lastSeq: 0,
    hydrating: true,
    historyThrough: 0,
    historicalMessageIds: new Set(),
    version: 0,
  }
  private pending: Snapshot | null = null
  private folding = false
  private listeners = new Set<() => void>()
  private socket: SessionSocket
  private raf: number | null = null
  private dirty = false
  private pauseTimer: number | null = null
  private paused = false
  private disposed = false
  private socketGeneration = 0
  private composerDraft: ComposerDraft = { text: '', attachments: [] }
  private factoryTask: FactoryTaskContext | null = null
  private factoryDrafts = new Map<string, ComposerDraft>()
  private startupOutbox: StartupOutbox
  private titleMessages: Map<string, string>
  private titleRequestIds = new Set<string>()
  private observedTitleGenerationCount = 0
  private startupOptionInFlight: string | null = null
  private startupMessagesInFlight = false

  constructor(
    public readonly sessionId: string,
    record?: SessionRecord,
  ) {
    this.startupOutbox = loadStartupOutbox(sessionId)
    this.titleMessages = loadTitleMessages(sessionId)
    this.observedTitleGenerationCount = record?.title_generation_count ?? 0
    // Seed visible state without advancing the durable replay cursor or sending prompts again.
    if (record)
      this.reduceFrame({
        seq: 0,
        ts: 0,
        frame: { type: 'bridge/record', session: record },
      })
    markStartup(sessionId, 'store_created')
    this.socket = this.connectSocket()
  }

  private connectSocket(): SessionSocket {
    const generation = ++this.socketGeneration
    return new SessionSocket(
      this.sessionId,
      (record) => {
        if (generation === this.socketGeneration) this.onFrame(record)
      },
      (status) => {
        if (generation === this.socketGeneration) this.onSocket(status)
      },
      this.pending?.lastSeq ?? this.snap.lastSeq,
    )
  }

  getSnapshot = (): Snapshot => this.snap
  /** Apply a local metadata response until the next authoritative record arrives. */
  acceptSessionRecord = (record: SessionRecord) => {
    if (record.id !== this.sessionId || this.disposed) return
    this.observeTitleGenerationCount(record.title_generation_count)
    this.snap = {
      ...this.snap,
      bridge: { ...this.snap.bridge, record },
    }
    this.schedule()
  }
  getComposerDraft = (): ComposerDraft => this.composerDraft
  saveComposerDraft = (draft: ComposerDraft) => {
    if (!this.disposed) {
      this.composerDraft = draft
      this.factoryDrafts.set(this.factoryTask?.id ?? '', draft)
    }
  }

  /** Keep an upload in the draft that selected it, even if task focus changes meanwhile. */
  appendComposerAttachment = (
    attachment: ComposerAttachment,
    taskId: string,
  ) => {
    if (this.disposed) return
    const selected = (this.factoryTask?.id ?? '') === taskId
    const draft = (selected
      ? this.composerDraft
      : this.factoryDrafts.get(taskId)) ?? {
      text: '',
      attachments: [],
    }
    const next = { ...draft, attachments: [...draft.attachments, attachment] }
    this.factoryDrafts.set(taskId, next)
    if (selected) this.composerDraft = next
  }

  getFactoryTask = (): FactoryTaskContext | null => this.factoryTask

  /** Switch explicit task scope without replacing another task's draft. */
  selectFactoryTask = (task: FactoryTaskContext | null) => {
    if (this.disposed) return
    this.factoryDrafts.set(this.factoryTask?.id ?? '', this.composerDraft)
    this.factoryTask = task
    this.composerDraft = this.factoryDrafts.get(task?.id ?? '') ?? {
      text: '',
      attachments: [],
    }
  }

  getStartupMessages = (): QueuedMessage[] => this.startupOutbox.messages

  /** Retain a directly-launched first prompt for subsequent title requests. */
  seedTitleMessage = (requestId: string, text: string) => {
    if (!text || this.disposed) return
    this.titleMessages.set(requestId, text)
    this.titleRequestIds.add(requestId)
    this.persistTitleMessages()
  }

  /** Hold a prompt locally until this startup socket can durably acknowledge it. */
  queueStartupMessage = (cmd: Extract<Command, { type: 'queue' }>): boolean => {
    const id = crypto.randomUUID()
    const content = 'content' in cmd ? cmd.content : null
    const text = 'text' in cmd ? cmd.text : ''
    this.startupOutbox = {
      ...this.startupOutbox,
      messages: [
        ...this.startupOutbox.messages,
        {
          id,
          startup: true,
          text: text || startupContentSummary(content),
          content,
          created_at: Date.now() / 1000,
          origin: 'user',
          send_mode:
            cmd.send_mode === 'queued' ? 'normal' : (cmd.send_mode ?? 'normal'),
        },
      ],
    }
    this.persistStartupOutbox()
    this.requestSessionTitle(id, text || startupContentSummary(content))
    this.awaitingActivity.add(id)
    this.schedule()
    this.flushStartupOutbox()
    return true
  }

  /** Keep a startup pill change ahead of every queued prompt until the runner is ready. */
  deferStartupOption = (configId: string, value: string | boolean): boolean => {
    const option = { id: crypto.randomUUID(), config_id: configId, value }
    this.startupOutbox = {
      ...this.startupOutbox,
      options: [
        ...this.startupOutbox.options.filter(
          (item) => item.config_id !== configId,
        ),
        option,
      ],
    }
    this.persistStartupOutbox()
    this.schedule()
    this.flushStartupOutbox()
    return true
  }

  /** Remove a locally held startup prompt before it is handed to the server. */
  removeStartupMessage = (id: string) => {
    this.startupOutbox = {
      ...this.startupOutbox,
      messages: this.startupOutbox.messages.filter((item) => item.id !== id),
    }
    this.awaitingActivity.delete(id)
    this.persistStartupOutbox()
    this.schedule()
  }

  subscribe = (fn: () => void): (() => void) => {
    if (this.pauseTimer !== null) {
      window.clearTimeout(this.pauseTimer)
      this.pauseTimer = null
    }
    this.listeners.add(fn)
    if (this.paused && !this.disposed) {
      this.paused = false
      this.socket = this.connectSocket()
    }
    return () => {
      this.listeners.delete(fn)
      if (this.listeners.size || this.disposed || this.pauseTimer !== null)
        return
      // React may immediately remount the same consumer; preserve that connection.
      this.pauseTimer = window.setTimeout(() => {
        this.pauseTimer = null
        if (this.listeners.size || this.disposed) return
        ++this.socketGeneration
        this.socket.close()
        this.paused = true
        this.onSocket('closed')
      }, 15_000)
    }
  }

  /** Place a client-side action failure in this session's timeline. */
  reportError = (error: string, command = 'session_action') => {
    const bridge = applyBridgeFrame(
      this.snap.bridge,
      {
        type: 'bridge/command_error',
        command,
        error,
        request_id: `local-${Date.now()}-${Math.random()}`,
      },
      this.snap.lastSeq + 0.5,
    )
    this.snap = { ...this.snap, bridge }
    this.schedule()
  }

  send = (cmd: Command): boolean => {
    if (
      this.snap.bridge.record?.migration_status === 'diagnostic' ||
      this.snap.hydrating
    )
      return false
    if (
      cmd.type === 'queue_send_now' &&
      this.startupOutbox.messages.some((item) => item.id === cmd.id)
    ) {
      // This request has no server queue entry until its batch is accepted.
      // Retry the immutable batch instead of steering an unknown request ID.
      this.flushStartupOutbox()
      return true
    }
    if (cmd.type === 'prompt' || cmd.type === 'queue' || cmd.type === 'steer') {
      const id = cmd.request_id ?? crypto.randomUUID()
      if (!this.socket.send({ ...cmd, request_id: id })) return false
      const content = 'content' in cmd ? cmd.content : null
      const text = 'text' in cmd ? cmd.text : ''
      this.outgoing.set(id, {
        id,
        text: text || startupContentSummary(content),
        content,
        created_at: Date.now() / 1000,
        origin: 'user',
        send_mode:
          cmd.send_mode ??
          (cmd.type === 'steer'
            ? 'steer'
            : this.snap.acp.running
              ? 'queued'
              : 'normal'),
        placement:
          cmd.type === 'steer' ||
          (!this.snap.acp.running &&
            ['ready', 'idle', 'running', 'requires_action'].includes(
              this.snap.bridge.status,
            ))
            ? 'chat'
            : 'queue',
      })
      if (this.outgoing.get(id)?.placement === 'chat')
        this.awaitingActivity.add(id)
      this.requestSessionTitle(id, text || startupContentSummary(content))
      this.schedule()
      return true
    }
    if (!this.socket.send(cmd)) return false
    if (cmd.type === 'queue_remove') {
      this.outgoing.delete(cmd.id)
      this.awaitingActivity.delete(cmd.id)
      this.schedule()
    }
    return true
  }

  private persistStartupOutbox() {
    if (
      !this.startupOutbox.options.length &&
      !this.startupOutbox.messages.length
    ) {
      sessionStorage.removeItem(startupOutboxKey(this.sessionId))
      return
    }
    sessionStorage.setItem(
      startupOutboxKey(this.sessionId),
      JSON.stringify(this.startupOutbox),
    )
  }

  private requestSessionTitle(requestId: string, text: string) {
    if (!text || this.titleMessages.has(requestId)) return
    const record = this.snap.bridge.record
    const knownCount = Math.max(
      record?.title_generation_count ?? 0,
      this.observedTitleGenerationCount,
    )
    if (record?.title_manual || knownCount + this.titleRequestIds.size >= 5)
      return
    this.seedTitleMessage(requestId, text)
    void api
      .generateSessionTitle(
        this.sessionId,
        requestId,
        this.titleInputMessages(),
      )
      // Socket bridge/record frames are ordered with renames and generated titles.
      // Do not fold this admission response: a delayed response can be older than a
      // manual rename or a newer generated title.
      .then(() => undefined)
      .catch(() => undefined)
  }

  private titleInputMessages(): string[] {
    const messages = new Map<string, string>()
    for (const [id, message] of Object.entries(
      this.getDisplayState().messages,
    )) {
      if (message.role !== 'user') continue
      const text = (message.content ?? [])
        .filter(
          (block): block is { type: 'text'; text: string } =>
            block.type === 'text' && typeof block.text === 'string',
        )
        .map((block) => block.text)
        .join('')
        .trim()
      if (text) messages.set(message.requestId ?? id, text)
    }
    for (const [id, text] of this.titleMessages) messages.set(id, text)
    return [...messages.values()]
  }

  private persistTitleMessages() {
    try {
      sessionStorage.setItem(
        titleMessagesKey(this.sessionId),
        JSON.stringify([...this.titleMessages.entries()]),
      )
    } catch {
      // Title context is an optional convenience. Storage quota or privacy mode
      // must never prevent the user message from being delivered.
    }
  }

  /** Send options first, then one FIFO message batch; receipts make reconnect retries safe. */
  private flushStartupOutbox = () => {
    if (
      this.disposed ||
      this.snap.hydrating ||
      this.snap.socket !== 'open' ||
      // A sleeping session rejects commands; the outbox waits for the wake.
      ['asleep', 'closed', 'exited'].includes(this.snap.bridge.status) ||
      (!this.startupOutbox.options.length &&
        !this.startupOutbox.messages.length)
    )
      return
    const option = this.startupOutbox.options[0]
    if (option) {
      if (this.startupOptionInFlight) return
      if (
        this.socket.send({
          type: 'set_config_option',
          config_id: option.config_id,
          value: option.value,
          request_id: option.id,
        })
      )
        this.startupOptionInFlight = option.id
      return
    }
    if (this.startupOutbox.messages.length && !this.startupMessagesInFlight)
      if (
        this.socket.send({
          type: 'queue_batch',
          items: this.startupOutbox.messages.map((item) =>
            item.content
              ? {
                  content: item.content,
                  request_id: item.id,
                  send_mode: item.send_mode,
                }
              : {
                  text: item.text,
                  request_id: item.id,
                  send_mode: item.send_mode,
                },
          ),
        })
      )
        this.startupMessagesInFlight = true
  }

  /** Merge a read-only ledger response without changing session activity. */
  observeCost = (value: number) => {
    const totalCostUsd = costHighWater(this.snap.bridge.totalCostUsd, value)
    if (totalCostUsd === this.snap.bridge.totalCostUsd) return
    this.snap = { ...this.snap, bridge: { ...this.snap.bridge, totalCostUsd } }
    this.schedule()
  }

  close() {
    this.disposed = true
    this.composerDraft = { text: '', attachments: [] }
    this.factoryDrafts.clear()
    this.factoryTask = null
    ++this.socketGeneration
    if (this.pauseTimer !== null) window.clearTimeout(this.pauseTimer)
    this.socket.close()
    if (this.raf) cancelAnimationFrame(this.raf)
    this.listeners.clear()
  }

  /** Fold one record; exposed for deterministic tests without a socket. */
  ingest = (rec: FrameRecord) => this.onFrame(rec)

  private onFrame = (rec: FrameRecord) => {
    const startupRejected =
      rec.frame.type === 'bridge/command_error' &&
      rec.frame.command === 'queue_batch' &&
      rec.seq > this.snap.lastSeq
    if (startupRejected) this.startupMessagesInFlight = false
    if (rec.frame.type === 'bridge/startup_command_accepted') {
      const id = rec.frame.request_id
      if (typeof id === 'string') {
        this.startupOutbox = {
          ...this.startupOutbox,
          options: this.startupOutbox.options.filter((item) => item.id !== id),
        }
        this.persistStartupOutbox()
        if (this.startupOptionInFlight === id) this.startupOptionInFlight = null
      }
    }
    if (rec.frame.type === 'bridge/queue_accepted') {
      const ids = new Set(
        Array.isArray(rec.frame.request_ids)
          ? rec.frame.request_ids.filter(
              (id): id is string => typeof id === 'string',
            )
          : [],
      )
      if (ids.size) {
        for (const id of ids) this.serverAccepted.add(id)
        for (const item of this.startupOutbox.messages)
          if (ids.has(item.id) && !this.outgoing.has(item.id))
            this.outgoing.set(item.id, {
              ...item,
              startup: true,
              placement: 'chat',
            })
        this.startupOutbox = {
          ...this.startupOutbox,
          messages: this.startupOutbox.messages.filter(
            (item) => !ids.has(item.id),
          ),
        }
        this.persistStartupOutbox()
        this.startupMessagesInFlight = false
      }
    }
    if (rec.frame.type === 'bridge/history_begin') {
      this.pending ??= this.snap
      this.snap = { ...this.snap, hydrating: true }
      this.schedule()
      return
    }
    if (rec.frame.type === 'bridge/history_batch') {
      for (const record of rec.frame.records as FrameRecord[])
        this.onFrame(record)
      return
    }
    if (rec.frame.type === 'bridge/history_complete') {
      if (!this.pending) return
      markStartup(this.sessionId, 'history_complete')
      const through = Number(rec.frame.through)
      this.snap = {
        ...this.pending,
        socket: this.snap.socket,
        version: this.snap.version,
        hydrating: false,
        bridge: {
          ...this.pending.bridge,
          totalCostUsd: costHighWater(
            this.pending.bridge.totalCostUsd,
            this.snap.bridge.totalCostUsd,
          ),
        },
        historyThrough: through,
        lastSeq: Math.max(this.pending.lastSeq, through),
      }
      this.snap = {
        ...this.snap,
        historicalMessageIds: new Set(
          this.snap.acp.timeline.map((item) => `${item.kind}:${item.id}`),
        ),
      }
      this.pending = null
      this.schedule()
      this.flushStartupOutbox()
      return
    }
    if (this.pending) {
      const visible = this.snap
      this.snap = this.pending
      this.folding = true
      try {
        this.reduceFrame(rec)
        this.pending = this.snap
      } finally {
        this.snap = visible
        this.folding = false
      }
    } else {
      if (rec.frame.type === 'bridge/hello')
        this.snap = { ...this.snap, hydrating: false }
      this.reduceFrame(rec)
    }
    // Keep rejected work retryable, but do not loop on the rejection itself.
    // A later readiness update, reconnect, or explicit Steer retries the batch.
    if (!startupRejected) this.flushStartupOutbox()
  }

  private reduceFrame = (rec: FrameRecord) => {
    const { seq } = rec
    if (seq > 0 && seq <= this.snap.lastSeq) return
    // WebSocket records carry the server's durable receipt time. If a local
    // producer did not provide one, capture it once as the event enters state.
    const timestamp =
      typeof rec.ts === 'number' && Number.isFinite(rec.ts) && rec.ts > 0
        ? rec.ts
        : Date.now() / 1000
    let { acp, bridge } = this.snap
    const fold = (frame: FrameRecord['frame']) => {
      if (frame.type === 'agent/batch' && Array.isArray(frame.frames)) {
        for (const child of frame.frames) fold(child as FrameRecord['frame'])
        return
      }
      observeStartupFrame(this.sessionId, frame)
      acp = applyAcpFrame(acp, frame, seq, timestamp)
      if (frame.type === 'bridge/queue' && Array.isArray(frame.items)) {
        const dispatching = frame.dispatching as QueuedMessage | undefined
        const queuedIds = new Set(
          (frame.items as QueuedMessage[]).map((item) => item.id),
        )
        if (dispatching?.id) queuedIds.add(dispatching.id)
        for (const id of this.awaitingActivity)
          if (
            this.serverAccepted.has(id) &&
            this.isStartupMessage(id) &&
            !queuedIds.has(id)
          )
            this.awaitingActivity.delete(id)
        if (dispatching?.id) {
          this.outgoing.set(dispatching.id, {
            ...dispatching,
            placement: 'chat',
          })
          this.serverAccepted.add(dispatching.id)
          this.awaitingActivity.add(dispatching.id)
        }
        for (const item of frame.items as QueuedMessage[]) {
          this.awaitingActivity.delete(item.id)
          const pending = this.outgoing.get(item.id)
          if (pending)
            this.outgoing.set(item.id, {
              ...pending,
              placement: this.isStartupMessage(item.id) ? 'chat' : 'queue',
            })
        }
      }
      if (
        frame.type === 'bridge/prompt_started' &&
        typeof frame.request_id === 'string'
      ) {
        const pending = this.outgoing.get(frame.request_id)
        if (pending) {
          this.outgoing.set(pending.id, { ...pending, placement: 'chat' })
          this.awaitingActivity.add(pending.id)
        }
      }
      if (
        frame.type === 'bridge/command_error' &&
        typeof frame.request_id === 'string'
      )
        this.outgoing.delete(frame.request_id)
      if (
        [
          'bridge/command_error',
          'bridge/prompt_finished',
          'bridge/prompt_rejected',
        ].includes(frame.type) &&
        typeof frame.request_id === 'string'
      )
        this.awaitingActivity.delete(frame.request_id)
      if (this.outgoing.size)
        for (const message of Object.values(acp.messages))
          if (message.role === 'user' && message.requestId)
            this.outgoing.delete(message.requestId)
      if (frame.type.startsWith('bridge/'))
        bridge = applyBridgeFrame(bridge, frame, seq, timestamp)
      if (
        frame.type === 'bridge/queue' ||
        frame.type === 'bridge/hello' ||
        frame.type === 'bridge/record'
      ) {
        for (const item of bridge.queue) {
          if ('kind' in item && item.kind === 'startup_command') continue
          this.serverAccepted.add(item.id)
          const startupMessage =
            item.startup ||
            this.isStartupMessage(item.id) ||
            ['created', 'starting'].includes(bridge.status)
          if (startupMessage) {
            this.outgoing.set(item.id, {
              ...item,
              startup: true,
              placement: 'chat',
            })
            // The creation request can already contain the first prompt. Keep
            // the indicator active from that inline handoff until the agent
            // reports running, rather than waiting for a later queue dispatch.
            this.awaitingActivity.add(item.id)
          }
        }
      }
      if (
        acp.running ||
        ['asleep', 'closed', 'exited', 'error', 'archived'].includes(
          bridge.status,
        )
      )
        this.awaitingActivity.clear()
    }
    fold(rec.frame)
    this.observeTitleGenerationCount(bridge.record?.title_generation_count)
    this.snap = {
      ...this.snap,
      acp,
      bridge,
      lastSeq: Math.max(this.snap.lastSeq, seq),
    }
    this.schedule()
  }

  private observeTitleGenerationCount(count: number | undefined) {
    if (count === undefined || count <= this.observedTitleGenerationCount)
      return
    let acknowledgements = count - this.observedTitleGenerationCount
    this.observedTitleGenerationCount = count
    for (const id of this.titleRequestIds) {
      this.titleRequestIds.delete(id)
      if (--acknowledgements === 0) return
    }
  }

  private onSocket = (status: SocketStatus) => {
    if (status !== 'open') {
      this.startupOptionInFlight = null
      this.startupMessagesInFlight = false
    }
    markStartup(this.sessionId, `socket_${status}`)
    this.snap = {
      ...this.snap,
      socket: status,
      hydrating: status === 'open' ? this.snap.hydrating : true,
    }
    this.schedule()
    this.flushStartupOutbox()
  }

  private schedule() {
    if (this.folding) return
    this.dirty = true
    if (this.raf !== null) return
    this.raf = requestAnimationFrame(() => {
      this.raf = null
      if (!this.dirty) return
      this.dirty = false
      this.snap = {
        ...this.snap,
        optimisticWorking: this.awaitingActivity.size > 0,
        version: this.snap.version + 1,
      }
      for (const fn of this.listeners) fn()
    })
  }
}

function startupContentSummary(content: unknown[] | null): string {
  return (
    content
      ?.filter(
        (part): part is { type: string; text?: unknown } =>
          !!part && typeof part === 'object' && 'type' in part,
      )
      .flatMap((part) =>
        part.type === 'text' && typeof part.text === 'string'
          ? [part.text]
          : [],
      )
      .join('\n') || '[Attachment]'
  )
}

/** Retain visited transcripts while subscriptions own their active socket connections. */
const stores = new Map<string, SessionStore>()

export function getSessionStore(
  id: string,
  record?: SessionRecord,
): SessionStore {
  let s = stores.get(id)
  if (!s) {
    s = new SessionStore(id, record)
    stores.set(id, s)
  }
  return s
}

export function dropSessionStore(id: string) {
  stores.get(id)?.close()
  stores.delete(id)
}

/** Discard private transcripts and disconnect sockets when identity changes. */
export function clearSessionStores() {
  for (const store of stores.values()) store.close()
  stores.clear()
}
